"use client";

import { useEffect, useState, useCallback } from "react";
import { useI18n } from "@/i18n/context";

const sectionIds = [
  "home",
  "context",
  "gaze",
  "experience",
  "identity",
  "when",
  "transparency",
  "contact",
] as const;

type NavKey = (typeof sectionIds)[number];

const navKeys: Record<NavKey, keyof typeof import("@/i18n/translations").translations.en.nav> = {
  home: "home",
  context: "context",
  gaze: "gaze",
  experience: "experience",
  identity: "identity",
  when: "when",
  transparency: "ethics",
  contact: "contact",
};

export function ScrollNav() {
  const { t } = useI18n();
  const [activeIndex, setActiveIndex] = useState(0);

  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY + window.innerHeight / 3;

    for (let i = sectionIds.length - 1; i >= 0; i--) {
      const el = document.getElementById(sectionIds[i]);
      if (el && el.offsetTop <= scrollY) {
        setActiveIndex(i);
        break;
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleClick = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav
      className="fixed left-[23px] top-1/2 -translate-y-1/2 z-50 flex flex-col gap-[18px] items-start"
      aria-label="Page sections"
    >
      {sectionIds.map((id, i) => {
        const label = t.nav[navKeys[id]];
        return (
          <button
            key={id}
            onClick={() => handleClick(id)}
            className="flex items-center gap-[8px] group cursor-pointer"
            aria-label={label}
            aria-current={i === activeIndex ? "true" : undefined}
          >
            {/* Dot */}
            <div
              className={`w-[10px] h-[10px] rounded-full border transition-all duration-300 ${
                i === activeIndex
                  ? "bg-bg-dark border-bg-dark scale-110"
                  : "bg-transparent border-text-secondary/50 group-hover:border-bg-dark"
              }`}
            />
            {/* Label — visible on active + hover */}
            <span
              className={`font-dm font-medium text-[10px] uppercase tracking-[1px] px-[5px] py-[3px] rounded-[5px] transition-all duration-300 whitespace-nowrap ${
                i === activeIndex
                  ? "bg-bg-dark text-white opacity-100 translate-x-0"
                  : "bg-bg-dark/80 text-white opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0"
              }`}
            >
              {label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
